import { Form } from "react-bootstrap";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import ReactSelect, { Props } from "react-select";
import { BaseInputProps, ErrorMessage } from ".";
import { usePermissions } from "../../../contexts";
import { CSSProperties, ReactNode, useMemo } from "react";
import { getDisplayedAccessor, getFormError } from "../../../helper-functions";
import { useFormSelectQuery } from "../../../queries";
import { FormSelectOption } from "../../../models";

interface SelectProps extends Props, BaseInputProps {
    label?: ReactNode;
    inlineLabel?: boolean;
    readOnly?: boolean;
    slug?: string;
    parentAccessor?: string;
    inGroup?: boolean;
    style?: CSSProperties;
    isInvalid?: boolean;
    customSort?: (a: { label: string; value: string }, b: { label: string; value: string }) => number;
}

export function Select({
    accessor,
    label,
    "aria-label": ariaLabel,
    inlineLabel,
    viewClaim,
    editClaim,
    readOnly,
    isDisabled,
    className,
    suppressMargin,
    options,
    placeholder,
    slug,
    parentAccessor,
    inGroup,
    style,
    isInvalid,
    styles,
    customSort,
    isClearable,
    ...rest
}: Readonly<SelectProps>) {
    const {
        control,
        formState: { errors },
    } = useFormContext();
    const selectedParentValue = useWatch({ control, name: parentAccessor ?? "" });
    const { getBySlugQuery } = useFormSelectQuery({ slug });
    const { hasPermission } = usePermissions();
    const formSelect = getBySlugQuery.data;
    const formSelectOptions = useMemo(() => formSelect?.formSelectOptions ?? [], [formSelect?.formSelectOptions]);
    const error = useMemo(() => getFormError(errors, accessor), [errors, accessor]);
    const isChild = !!parentAccessor;

    const isReadOnly = useMemo(
        () => (editClaim ? !hasPermission(editClaim) : readOnly),
        [readOnly, editClaim, hasPermission]
    );

    const parentOption = useMemo(() => {
        if (parentAccessor && selectedParentValue) {
            return formSelectOptions.find((option) => option.value === selectedParentValue);
        }
        return undefined;
    }, [parentAccessor, selectedParentValue, formSelectOptions]);

    const calculatedStyles = useMemo(() => {
        if (styles) return styles;
        return {
            control: (base) => ({
                ...base,
                borderColor: isInvalid ? "var(--bs-danger)" : base.borderColor,
                ...style,
            }),
        };
    }, [isInvalid, style, styles]);

    const defaultSort = (a: { label: string; value: string }, b: { label: string; value: string }) => {
        return a.label.localeCompare(b.label);
    };

    const renderedOptions = useMemo(() => {
        if (options) return options;
        let result: FormSelectOption[] = [];
        if (!formSelect)
            return [
                {
                    label: "Options not configured",
                    value: "",
                    isDisabled: true,
                },
            ];
        else if (!isChild) {
            result = formSelectOptions.filter((option) => !option.parentFormSelectOptionId);
        } else if (parentOption) {
            result = formSelectOptions.filter((option) => option.parentFormSelectOptionId === parentOption.id);
        } else {
            return [
                {
                    label: formSelect?.name ? `Select an option for ${formSelect.name}` : "Select an option above",
                    value: "",
                    isDisabled: true,
                },
            ];
        }

        return result
            .map((option) => ({
                label: option.displayName,
                value: option.value,
            }))
            .sort(customSort || defaultSort);
    }, [formSelect, formSelectOptions, isChild, options, parentOption, customSort]);

    if (!!viewClaim && !hasPermission(viewClaim)) return null;

    if (!inGroup)
        return (
            <Form.Group
                controlId={accessor}
                className={`${!suppressMargin && "mb-2"} ${className}`}
                key={`select-${accessor}`}
                title={`${getDisplayedAccessor(accessor)}: ${slug ?? ""}`}
            >
                <div className={inlineLabel ? "d-flex align-items-center" : ""}>
                    {!!label && (
                        <Form.Label style={{ whiteSpace: "nowrap" }} className={inlineLabel ? "me-2" : ""}>
                            {label} {rest.required && <span className="text-danger">*</span>}
                        </Form.Label>
                    )}
                    <Controller
                        control={control}
                        name={accessor}
                        render={({ field: { value, onChange, ...otherFields } }) => {
                            const calculatedValue = rest.isMulti
                                ? (renderedOptions?.filter((option: any) => value?.includes(option.value)) ?? [])
                                : (renderedOptions?.find((option: any) => option.value === value) ?? "");
                            return (
                                <div className="d-flex align-items-center">
                                    <ReactSelect
                                        id={accessor}
                                        isDisabled={isReadOnly ?? isDisabled}
                                        aria-readonly={isReadOnly}
                                        aria-label={ariaLabel ?? accessor}
                                        options={renderedOptions}
                                        placeholder={placeholder}
                                        value={calculatedValue}
                                        onChange={(val: any) => onChange(val?.value)}
                                        className="flex-grow-1"
                                        styles={calculatedStyles}
                                        isClearable={isClearable ?? !isReadOnly}
                                        {...otherFields}
                                        {...rest}
                                    />
                                </div>
                            );
                        }}
                    />
                </div>
                <ErrorMessage error={error} />
            </Form.Group>
        );

    return (
        <>
            <Controller
                control={control}
                name={accessor}
                key={`select-${accessor}`}
                render={({ field: { value, onChange, ...otherFields } }) => {
                    const calculatedValue = rest.isMulti
                        ? (renderedOptions?.filter((option: any) => value?.includes(option.value)) ?? [])
                        : (renderedOptions?.find((option: any) => option.value === value) ?? "");
                    return (
                        <div
                            className="d-flex align-items-center"
                            title={`${getDisplayedAccessor(accessor)}: ${slug ?? ""}`}
                        >
                            <ReactSelect
                                id={accessor}
                                isDisabled={isReadOnly ?? isDisabled}
                                aria-readonly={isReadOnly}
                                aria-label={ariaLabel ?? accessor}
                                options={renderedOptions}
                                placeholder={placeholder}
                                value={calculatedValue}
                                onChange={(val: any) => onChange(val?.value)}
                                className="flex-grow-1"
                                styles={calculatedStyles}
                                isClearable={isClearable ?? !isReadOnly}
                                {...otherFields}
                                {...rest}
                            />
                        </div>
                    );
                }}
            />
            <ErrorMessage error={error} />
        </>
    );
}
